import type { Feature, Polygon } from 'geojson';
import type { WellQueryResult } from './types';

/**
 * Ray-casting point-in-polygon test against a polygon's outer ring.
 * @param {number[]} point - [lng, lat]
 * @param {Polygon} polygon - The clipping polygon
 * @return {boolean} Whether the point lies inside the outer ring
 */
export const pointInPolygon = (point: number[], polygon: Polygon): boolean => {
  const ring = polygon.coordinates[0];
  if (!ring) return false;
  const [x, y] = point;
  let inside = false;
  let j = ring.length - 1;
  for (let i = 0; i < ring.length; i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
    j = i;
  }
  return inside;
};

/**
 * Flattens a wellbore line feature (survey / stick) to its [lng, lat] vertices.
 * Returns [] for a missing feature or a non-line geometry, so it contributes
 * nothing rather than throwing.
 * @param {Feature | null | undefined} feature - The line feature
 * @return {Array<number[]>} The line's vertices
 */
const lineVertices = (feature: Feature | null | undefined): number[][] => {
  const geometry = feature?.geometry;
  if (!geometry) return [];
  if (geometry.type === 'LineString') return geometry.coordinates;
  if (geometry.type === 'MultiLineString') return geometry.coordinates.flat(1);
  return [];
};

/**
 * Whether any of a well's rendered geometry falls inside the polygon. Tests the
 * surface/bottom-hole points AND the survey/stick line vertices: the backend
 * omits the hole points for non-producing bores (which render from the survey
 * line only), so a point-only test would drop every such well from the filter.
 * @param {WellQueryResult} well - The well geometry
 * @param {Polygon} polygon - The clipping polygon (visible extent or drawn AOI)
 * @return {boolean} Whether the well is inside the polygon
 */
export const wellInPolygon = (
  well: WellQueryResult,
  polygon: Polygon
): boolean =>
  (well.wellBores ?? []).some((wb) => {
    const points: number[][] = [];
    if (wb.surfaceHole) points.push(wb.surfaceHole.coordinates);
    if (wb.bottomHole) points.push(wb.bottomHole.coordinates);
    points.push(...lineVertices(wb.survey));
    points.push(...lineVertices(wb.stick));
    return points.some((p) => pointInPolygon(p, polygon));
  });
